import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  setDoc,
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeOrganizationId } from '../utils/organizationAutoHeal';

// Generic CRUD operations
export class FirebaseService {
  constructor(collectionName) {
    this.collectionName = collectionName;
    this.collectionRef = collection(db, collectionName);
  }

  // Create a new document
  async create(data) {
    try {
      const docData = {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // If data has an ID, use setDoc to create/replace with that specific ID
      // This prevents duplicates when importing data with predefined IDs
      if (data.id) {
        const docRef = doc(db, this.collectionName, data.id);
        await setDoc(docRef, docData);
        return { id: data.id, ...docData };
      } else {
        // If no ID provided, use addDoc to auto-generate one
        const docRef = await addDoc(this.collectionRef, docData);
        return { id: docRef.id, ...docData };
      }
    } catch (error) {
      console.error(`Error creating ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Read a single document
  async getById(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error(`Document ${id} not found`);
      }
    } catch (error) {
      console.error(`Error getting ${this.collectionName} by ID:`, error);
      throw error;
    }
  }

  // Read all documents
  async getAll() {
    try {
      const querySnapshot = await getDocs(this.collectionRef);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error getting all ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Read documents with query
  async getWhere(field, operator, value) {
    try {
      const q = query(this.collectionRef, where(field, operator, value));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error querying ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Update a document (upsert - creates if doesn't exist)
  async update(id, data) {
    try {
      const docRef = doc(db, this.collectionName, id);
      
      // Check if document exists first
      const docSnap = await getDoc(docRef);
      const isNewDoc = !docSnap.exists();
      
      const updateData = {
        ...data,
        updatedAt: serverTimestamp(),
        // Add createdAt only if it's a new document
        ...(isNewDoc && { createdAt: serverTimestamp() })
      };
      
      // Use setDoc with merge to create document if it doesn't exist
      await setDoc(docRef, updateData, { merge: true });
      return { id, ...updateData };
    } catch (error) {
      console.error(`Error updating ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Delete a document
  async delete(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
      return { id };
    } catch (error) {
      console.error(`Error deleting ${this.collectionName}:`, error);
      throw error;
    }
  }

  // Real-time listener
  subscribeToCollection(callback, queryConstraints = []) {
    try {
      const q = query(this.collectionRef, ...queryConstraints);
      return onSnapshot(q, (querySnapshot) => {
        const data = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(data);
      });
    } catch (error) {
      console.error(`Error setting up listener for ${this.collectionName}:`, error);
      throw error;
    }
  }
}

// Specific services for each collection
export const organizationService = new FirebaseService('organizations');
export const locationService = new FirebaseService('locations');
export const buildingService = new FirebaseService('buildings');
export const floorService = new FirebaseService('floors');
export const departmentService = new FirebaseService('departments');
export const roomService = new FirebaseService('rooms');
export const bedService = new FirebaseService('beds');
export const personnelService = new FirebaseService('personnel');
export const equipmentService = new FirebaseService('equipment');
export const biologicalEntityService = new FirebaseService('biological_entities');
export const ipcConfigurationService = new FirebaseService('ipcConfiguration');
export const userService = new FirebaseService('users');
export const auditLogService = new FirebaseService('auditLogs');

// Specialized query methods
export const clinicalStructureService = {
  // Get locations by organization
  async getLocationsByOrganization(organizationId) {
    return locationService.getWhere('organizationId', '==', organizationId);
  },

  // Get buildings by location
  async getBuildingsByLocation(locationId) {
    return buildingService.getWhere('locationId', '==', locationId);
  },

  // Get floors by building
  async getFloorsByBuilding(buildingId) {
    return floorService.getWhere('buildingId', '==', buildingId);
  },

  // Get departments by floor
  async getDepartmentsByFloor(floorId) {
    return departmentService.getWhere('floorId', '==', floorId);
  },

  // Get departments by building (for cross-floor departments)
  async getDepartmentsByBuilding(buildingId) {
    return departmentService.getWhere('buildingId', '==', buildingId);
  },

  // Get rooms by department
  async getRoomsByDepartment(departmentId) {
    return roomService.getWhere('departmentId', '==', departmentId);
  },

  // Get beds by room
  async getBedsByRoom(roomId) {
    return bedService.getWhere('roomId', '==', roomId);
  },

  // Get personnel by department
  async getPersonnelByDepartment(departmentId) {
    return personnelService.getWhere('departmentId', '==', departmentId);
  },

  // Get equipment by department
  async getEquipmentByDepartment(departmentId) {
    return equipmentService.getWhere('departmentId', '==', departmentId);
  },

  /**
   * Load full clinical structure for an organization (locations → buildings → floors → departments → rooms → beds, equipment).
   * Includes legacy recovery when organizationId was stored incorrectly on locations (Setup Wizard bug).
   */
  async getStructureByOrganization(organizationId) {
    const empty = {
      organizations: [],
      locations: [],
      buildings: [],
      floors: [],
      departments: [],
      rooms: [],
      beds: [],
      equipment: [],
      personnel: []
    };

    const orgId = normalizeOrganizationId(organizationId);
    if (!orgId) return empty;

    let locations = await this.getLocationsByOrganization(orgId);

    if (locations.length === 0) {
      console.warn(
        '[clinicalStructureService] No locations matched organizationId query; running recovery for',
        orgId
      );

      const allLocations = await locationService.getAll();
      const orgSnap = await getDocs(query(collection(db, 'organizations'), limit(2)));
      const isSingleOrg = orgSnap.size === 1;

      locations = allLocations.filter((loc) => {
        const locOrg = normalizeOrganizationId(loc.organizationId);
        return locOrg === orgId || (isSingleOrg && !locOrg);
      });

      if (locations.length === 0) {
        const allBuildings = await buildingService.getAll();
        const linkedLocationIds = [
          ...new Set(allBuildings.map((b) => b.locationId).filter(Boolean))
        ];
        if (linkedLocationIds.length > 0) {
          const linked = await Promise.all(
            linkedLocationIds.map(async (id) => {
              try {
                return await locationService.getById(id);
              } catch {
                return null;
              }
            })
          );
          locations = linked.filter(Boolean);
        }
      }

      for (const loc of locations) {
        const locOrg = normalizeOrganizationId(loc.organizationId);
        if (locOrg !== orgId) {
          try {
            await locationService.update(loc.id, { organizationId: orgId });
            loc.organizationId = orgId;
            console.info('[clinicalStructureService] Healed location organizationId:', loc.id);
          } catch (e) {
            console.warn('[clinicalStructureService] Could not heal location', loc.id, e?.message);
          }
        }
      }
    }

    const locationIds = locations.map((l) => l.id);
    let buildings = locationIds.length
      ? (await Promise.all(locationIds.map((lid) => buildingService.getWhere('locationId', '==', lid)))).flat()
      : [];

    if (buildings.length === 0 && locations.length === 0) {
      const orgSnap = await getDocs(query(collection(db, 'organizations'), limit(2)));
      if (orgSnap.size === 1) {
        console.warn('[clinicalStructureService] Single-tenant full-collection recovery');
        const [allLocs, allB, allF, allD, allR, allBe, allEq, allP] = await Promise.all([
          locationService.getAll(),
          buildingService.getAll(),
          floorService.getAll(),
          departmentService.getAll(),
          roomService.getAll(),
          bedService.getAll(),
          equipmentService.getAll(),
          personnelService.getAll()
        ]);
        return {
          organizations: [],
          locations: allLocs,
          buildings: allB,
          floors: allF,
          departments: allD,
          rooms: allR,
          beds: allBe,
          equipment: allEq,
          personnel: allP
        };
      }
    }

    const buildingIds = buildings.map((b) => b.id);
    const floors = buildingIds.length
      ? (await Promise.all(buildingIds.map((bid) => floorService.getWhere('buildingId', '==', bid)))).flat()
      : [];
    const floorIds = floors.map((f) => f.id);
    const departments = floorIds.length
      ? (await Promise.all(floorIds.map((fid) => departmentService.getWhere('floorId', '==', fid)))).flat()
      : [];
    const departmentIds = departments.map((d) => d.id);
    const [rooms, personnel, equipment] = departmentIds.length
      ? await Promise.all([
          Promise.all(departmentIds.map((did) => roomService.getWhere('departmentId', '==', did))).then((arr) => arr.flat()),
          Promise.all(departmentIds.map((did) => personnelService.getWhere('departmentId', '==', did))).then((arr) => arr.flat()),
          Promise.all(departmentIds.map((did) => equipmentService.getWhere('departmentId', '==', did))).then((arr) => arr.flat())
        ])
      : [[], [], []];
    const roomIds = rooms.map((r) => r.id);
    const beds = roomIds.length
      ? (await Promise.all(roomIds.map((rid) => bedService.getWhere('roomId', '==', rid)))).flat()
      : [];
    return {
      organizations: [],
      locations,
      buildings,
      floors,
      departments,
      rooms,
      beds,
      equipment,
      personnel
    };
  }
};

export const biologicalEntityServiceExtended = {
  // Get entities by category
  async getEntitiesByCategory(category) {
    return biologicalEntityService.getWhere('category', '==', category);
  },

  // Get entities by priority
  async getEntitiesByPriority(priority) {
    return biologicalEntityService.getWhere('priority', '==', priority);
  },

  // Get entities by detection status
  async getEntitiesByDetectionStatus(status) {
    return biologicalEntityService.getWhere('detectionStatus', '==', status);
  },

  // Search entities by name
  async searchEntitiesByName(searchTerm) {
    // Note: Firestore doesn't support full-text search natively
    // This is a simple prefix search - for production, consider Algolia or similar
    const allEntities = await biologicalEntityService.getAll();
    return allEntities.filter(entity => 
      entity.scientificName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entity.commonName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  },

  // Get high-risk entities (RPN > 500)
  async getHighRiskEntities() {
    const allEntities = await biologicalEntityService.getAll();
    return allEntities.filter(entity => entity.rpnScore > 500);
  },

  // Get recently detected entities
  async getRecentlyDetectedEntities(days = 30) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    const allEntities = await biologicalEntityService.getAll();
    return allEntities.filter(entity => {
      if (!entity.lastDetection) return false;
      const lastDetection = new Date(entity.lastDetection);
      return lastDetection >= cutoffDate;
    });
  }
};

export const ipcConfigurationServiceExtended = {
  // Get IPC configuration by organization
  async getByOrganization(organizationId) {
    const configs = await ipcConfigurationService.getWhere('organizationId', '==', organizationId);
    return configs[0] || null;
  },

  // Update IPC configuration
  async updateByOrganization(organizationId, data) {
    const existing = await this.getByOrganization(organizationId);
    if (existing) {
      return ipcConfigurationService.update(existing.id, data);
    } else {
      return ipcConfigurationService.create({
        organizationId,
        ...data
      });
    }
  }
};

// Audit logging service
export const auditLogServiceExtended = {
  async logAction(userId, action, resource, resourceId, changes = null, organizationId = null) {
    try {
      const logData = {
        userId,
        action,
        resource,
        resourceId,
        changes,
        organizationId,
        timestamp: serverTimestamp(),
        ipAddress: 'localhost', // In production, get from request
        userAgent: navigator.userAgent
      };
      
      return auditLogService.create(logData);
    } catch (error) {
      console.error('Error logging audit action:', error);
      // Don't throw - audit logging shouldn't break the main flow
    }
  }
};




